import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toProdProfile } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ProfileFields } from "@/components/ProfileFields";
import { PhotoManager } from "@/components/PhotoManager";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ArrowRight, Eye, PieChart, PlusCircle, LogOut, ShieldCheck, X, Sparkles } from "lucide-react";

export default function Onboarding() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(user?.profile || {});
  const [saving, setSaving] = useState(false);
  const [ack, setAck] = useState(true);
  const dirty = useRef(false);
  const [consent, setConsent] = useState(false);
  const set = (k, v) => { dirty.current = true; setP((prev) => ({ ...prev, [k]: v })); };

  // Auto-save (debounced) so members never lose progress while scrolling.
  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(async () => {
      try { await api.put("/profile", toProdProfile(p)); dirty.current = false; toast.success("Progress saved", { id: "autosave", icon: "✓" }); }
      catch { /* keep dirty; will retry on next change */ }
    }, 1600);
    return () => clearTimeout(t);
  }, [p]);

  const completeness = Number(user?.raw?.profileCompleteness ?? 0);
  const missing = user?.raw?.completenessMissing || [];
  const [showResume, setShowResume] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("nikah_onb_seen") && completeness < 100) setShowResume(true);
    localStorage.setItem("nikah_onb_seen", "1");
  }, []); // eslint-disable-line

  const save = async (go) => {
    setSaving(true);
    try { await api.put("/profile", toProdProfile(p)); await refreshUser(); toast.success("Profile saved"); if (go) navigate("/discovery", { replace: true }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-ivory pattern-bg pb-28">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="mb-5 flex items-center justify-between">
          <Logo size="md" />
          <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-100" data-testid="onboarding-logout"><LogOut className="h-4 w-4" /> Log out</button>
        </div>

        {showResume && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-3xl border border-amber-300/60 bg-amber-50 p-4 animate-fade-up" data-testid="resume-banner">
            <p className="flex items-center gap-2 text-sm text-amber-900"><Sparkles className="h-4 w-4 shrink-0 text-amber-600" /> <span><b>Welcome back</b> — pick up where you left off. Your profile is {completeness}% complete.</span></p>
            <button onClick={() => setShowResume(false)} className="rounded-full p-1 text-amber-700 hover:bg-amber-100" data-testid="resume-dismiss" aria-label="Dismiss"><X className="h-4 w-4" /></button>
          </div>
        )}

        <div className="mb-6">
          <h1 className="font-serif text-4xl font-bold text-emerald-950">Create your profile</h1>
          <p className="mt-1 max-w-md text-slate-500">Thoughtful details help us find sincere, compatible matches — inshaAllah.</p>
        </div>

        {/* Progress */}
        <div className="mb-5 rounded-3xl border border-emerald-900/10 bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 font-serif text-lg font-bold text-emerald-950"><PieChart className="h-5 w-5 text-amber-600" /> Profile {completeness}% complete</p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-mint"><div className="h-full rounded-full bg-amber-600 transition-all" style={{ width: `${completeness}%` }} /></div>
          {missing.length > 0 && <div className="mt-3 space-y-1.5">{missing.map((m) => <p key={m} className="flex items-center gap-2 text-sm text-slate-500"><PlusCircle className="h-4 w-4 text-slate-300" /> {m}</p>)}</div>}
        </div>

        {/* Profile photos */}
        <div className="mb-5 rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm sm:p-7">
          <h2 className="font-serif text-2xl font-bold text-emerald-950">Profile photos</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">Add up to 5 clear single-person portraits — face centered, no sunglasses or masks. Our AI checks quality only, never appearance. Your first photo is your primary.</p>
          <div className="mt-4 flex items-start gap-2 rounded-2xl bg-mint/60 p-4 text-sm text-emerald-900"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> Your photos are shown with a small Nikah Match watermark to discourage misuse. Your originals are stored privately and never shared publicly.</div>
          <label className="mt-4 flex items-center gap-2.5 text-sm font-semibold text-emerald-950"><Checkbox checked={ack} onCheckedChange={setAck} data-testid="photo-ack" /> Photo notice acknowledged</label>
          <div className="mt-5"><PhotoManager /></div>
        </div>

        <ProfileFields p={p} set={set} />

        {/* Mutual-interest consent */}
        <label className="mt-5 flex items-start gap-3 rounded-3xl border border-emerald-900/10 bg-white p-5 text-sm text-slate-700 shadow-sm" data-testid="onboarding-consent-row">
          <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="onboarding-consent" className="mt-0.5" />
          <span>I understand and consent to sharing my email address and phone number with another member only after we both express mutual interest.</span>
        </label>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-emerald-900/10 bg-ivory/95 glass">
        <div className="mx-auto flex max-w-3xl gap-3 px-4 py-3">
          <Button variant="outline" onClick={() => save(false).then(() => navigate(`/profiles/${user?.id}`))} className="flex-1 rounded-full border-emerald-800/30 py-6 text-emerald-900 hover:bg-mint" data-testid="onboarding-preview-btn"><Eye className="mr-2 h-4 w-4" /> Preview My Profile</Button>
          <Button onClick={() => { if (!consent) return toast.error("Please tick the consent box to continue"); save(true); }} disabled={saving || !consent} className="flex-1 rounded-full bg-emerald-800 py-6 text-white hover:bg-emerald-900 disabled:opacity-60" data-testid="onboarding-continue-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="mr-2 h-4 w-4" /> Continue</>}</Button>
        </div>
      </div>
    </div>
  );
}
